import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Select as UnstyledSelect } from '@streamr/streamr-layout'
import { Regular, MqTablet, MqDesktop, Sans, Medium } from 'streamr-ui/css/consts'
import useIsMounted from '~hooks/useIsMounted'
import ButtonWrapper from '~shared/ButtonWrapper'
import { Checkmark as UnstyledCheckmark } from '~shared/Glyph'
import urls from '~utils/urls'
import {
    Link,
    LinkLook,
    PrimaryButton,
    Hint,
    TextField,
    Label,
    Radio,
    Checkbox as PrestyledCheckbox,
    HubSpotFormContainer as PrestyledHubSpotFormContainer,
} from 'streamr-ui'

const Background = styled.div`
    background-color: #f8f8f8;
`

const Container = styled.div`
    color: #323232;
    padding: 5.5rem 2rem 5.5rem 2rem;

    @media ${MqTablet} {
        padding: 6.5rem 0;
        max-width: 31rem;
        margin: 0 auto;
    }

    @media ${MqDesktop} {
        padding: 8.5rem 0;
        max-width: 46rem;
        margin: 0 auto;
    }
`

const Heading = styled.h2`
    font-family: ${Sans};
    font-weight: ${Regular};
    font-size: 28px;
    line-height: 40px;
    display: flex;
    justify-content: center;
    margin: 0;

    a {
        color: inherit !important;
        text-decoration: none !important;
    }

    @media ${MqTablet} {
        font-size: 32px;
        line-height: 40px;
    }

    @media ${MqDesktop} {
        font-size: 40px;
        line-height: 48px;
    }
`

const Message = styled.div`
    display: flex;
    font-size: 16px;
    line-height: 24px;
    margin-top: 16px;
    height: 24px;
    transition: 200ms opacity;
    opacity: ${({ visible }) => (visible ? '1' : '0')};
`

const Form = styled.form`
    margin-top: 6.375rem;

    > ${Label} {
        margin-bottom: 40px;
    }

    > ${ButtonWrapper} {
        margin-top: 60px;
    }

    > ${ButtonWrapper}, > ${ButtonWrapper} ${PrimaryButton} {
        width: 100%;
    }

    @media ${MqTablet} {
        > ${ButtonWrapper} {
            width: auto;
        }
    }

    @media ${MqDesktop} {
        padding: 0 100px;
    }
`

const Select = styled(UnstyledSelect)`
    font-weight: ${Regular};

    ${({ hasError }) =>
        !!hasError &&
        css`
            border: 1px solid #ff5c00 !important;
            border-radius: 4px;
        `}
`

const TextAreaCss = css`
    min-height: 168px;
    line-height: 24px;
`

const CheckboxLabel = styled.label`
    color: #525252;
    display: block;
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 8px;
`

const Checkbox = styled(PrestyledCheckbox)`
    margin-right: 20px;
    margin-top: -2px;
    vertical-align: middle;
`

const Checkmark = styled(UnstyledCheckmark)`
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-self: center;
    margin: -2px 6px 0 -4px;
`

function toOptions(arr) {
    return arr.map((o) => ({
        value: o,
        label: o,
    }))
}

const categories = {
    'Quick Response': toOptions([
        'Demo app',
        'Hackathon or event',
        'Bugfix or code contribution',
        'Marketing initiative UI/UX design',
        'Streamr stack experiment',
        'Sharable canvas demo',
        'Research project',
        'Integration',
        'Client library',
        'Data Union seed funding',
        'Audit',
        'Other',
    ]),
    Project: toOptions([
        'Demo app',
        'Client library',
        'Hackathon, event',
        'Integration',
        'Research project',
        'Data Union growth funding',
        'Enterprise pilot',
        'Token integration',
        'Other',
    ]),
}

const initialState = {
    name: '',
    grantType: 'Quick Response',
    projectCategory: undefined,
    projectName: '',
    projectDescription: '',
    background: '',
    grantAmount: '',
    timeline: '',
    contactEmail: '',
    termsApproved: false,
}

const NEWSLETTER_FORM_CONTAINER_ID = 'dataFundHSF'

function validate(data, { sideEffect } = {}) {
    const result = {}

    Object.keys(data).forEach((key) => {
        const field = data[key]

        if (!field) {
            result[key] = 'Please fill in this field before submitting'
            return
        }

        if (key === 'contactEmail' && !/^[^@\s]+@[^@\s]+\.[^.@\s]+$/.test(field)) {
            result[key] = 'Invalid email address'
            return
        }
    })

    if (typeof sideEffect === 'function') {
        sideEffect(result)
    }

    return result
}

function submitHubspotForm(value) {
    const form = document.querySelector(`#${NEWSLETTER_FORM_CONTAINER_ID} form`)

    if (!form) {
        console.warn('Unable to find the newsletter form element')
        return
    }

    const emailField = form.querySelector('[type=email]')

    if (!emailField) {
        console.warn('Unable to find the newsletter email address field')
        return
    }

    emailField.value = value

    form.submit()
}

const HubSpotFormContainer = styled(PrestyledHubSpotFormContainer)`
    height: 0;
    overflow: hidden;
    visibility: hidden;
`

const ApplicationForm = (props) => {
    const isMounted = useIsMounted()
    const [submitting, setSubmitting] = useState(false)
    const [resolution, setResolution] = useState()

    const [formData, setFormData] = useState(initialState)

    const {
        name,
        grantType,
        projectCategory,
        projectName,
        projectDescription,
        background,
        grantAmount,
        timeline,
        contactEmail,
        termsApproved,
    } = formData

    const [errors, setErrors] = useState({})

    const [newsletterSignup, setNewsletterSignup] = useState(false)

    const cats = categories[grantType] || []

    async function onSubmit(e) {
        e.preventDefault()

        setResolution(undefined)

        const currentErrors = validate(formData, {
            sideEffect: (errs) => void setErrors(errs),
        })

        if (Object.keys(currentErrors).length) {
            return
        }

        const { target: form } = e

        setSubmitting(true)

        try {
            await fetch(form.action, {
                body: JSON.stringify(formData),
                headers: {
                    'Content-Type': 'application/json',
                },
                method: 'POST',
            })

            if (newsletterSignup) {
                submitHubspotForm(formData.contactEmail)
            }

            if (isMounted()) {
                setResolution(true)
                setFormData(initialState)
            }
        } catch (err) {
            const response = err.response || {}

            if (isMounted()) {
                if (response.status === 422) {
                    const message = `${response.headers['x-streamr-error']}`
                    setResolution(message)
                } else {
                    setResolution('An error occured.')
                }
            }
        } finally {
            if (isMounted()) {
                setSubmitting(false)
            }
        }
    }

    function setter(key) {
        return (value) => {
            const data = {
                [key]: value,
            }

            setFormData((fd) => ({
                ...fd,
                ...data,
            }))
        }
    }

    return (
        <Background {...props}>
            <Container>
                <Heading $appear>
                    <a href="#application-form">Application Form</a>
                </Heading>
                <Form action="/data-fund-application" onSubmit={onSubmit}>
                    <FieldWrap $appear>
                        <Label value="Your name">
                            <TextField
                                invalid={!!errors.name}
                                value={name}
                                onCommit={setter('name')}
                                placeholder="Or your team's name"
                            />
                            {!!errors.name && <Hint>{errors.name}</Hint>}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Grant type">
                            <Radio
                                options={['Quick Response', 'Project']}
                                value={grantType}
                                onChange={(value) => {
                                    setFormData((fd) => {
                                        const opt = (categories[value] || []).find(
                                            ({ value }) => value === fd.projectCategory,
                                        )

                                        return {
                                            ...fd,
                                            grantType: value,
                                            projectCategory: opt ? fd.projectCategory : undefined,
                                        }
                                    })
                                }}
                            />
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear $bringToFront>
                        <Label value="Project category">
                            <Select
                                placeholder="Project category"
                                options={cats}
                                value={cats.find((o) => o.value === projectCategory) || ''}
                                onChange={({ value }) => {
                                    setter('projectCategory')(value)
                                }}
                                hasError={!!errors.projectCategory}
                            />
                            {!!errors.projectCategory && <Hint>{errors.projectCategory}</Hint>}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Project name">
                            <TextField
                                placeholder="Give your proposal a name"
                                value={projectName}
                                onCommit={setter('projectName')}
                                invalid={!!errors.projectName}
                            />
                            {!!errors.projectName && <Hint>{errors.projectName}</Hint>}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Project description">
                            <TextField
                                tag="textarea"
                                placeholder="Describe your project in as much detail as you can, 
                                    including any supporting material, links, and documents. For
                                    Project grants, include milestones if you can."
                                value={projectDescription}
                                onCommit={setter('projectDescription')}
                                css={TextAreaCss}
                                invalid={!!errors.projectDescription}
                            />
                            {!!errors.projectDescription && (
                                <Hint>{errors.projectDescription}</Hint>
                            )}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Background / Previous work">
                            <TextField
                                tag="textarea"
                                placeholder="Links to anything that demonstrates your ability to
                                    deliver the project. Github repos, live apps, LinkedIn profiles
                                    or whatever you think is appropriate."
                                value={background}
                                onCommit={setter('background')}
                                css={TextAreaCss}
                                invalid={!!errors.background}
                            />
                            {!!errors.background && <Hint>{errors.background}</Hint>}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Grant amount requested">
                            <TextField
                                placeholder="Amount you'd like for the project in USD, to be paid in DATA or DAI"
                                value={grantAmount}
                                onCommit={setter('grantAmount')}
                                invalid={!!errors.grantAmount}
                            />
                            {!!errors.grantAmount && <Hint>{errors.grantAmount}</Hint>}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Estimated timeline">
                            <TextField
                                placeholder="How long do you think it will take?"
                                value={timeline}
                                onCommit={setter('timeline')}
                                invalid={!!errors.timeline}
                            />
                            {!!errors.timeline && <Hint>{errors.timeline}</Hint>}
                        </Label>
                    </FieldWrap>
                    <FieldWrap $appear>
                        <Label value="Contact email">
                            <TextField
                                placeholder="you@project.xyz"
                                value={contactEmail}
                                onCommit={setter('contactEmail')}
                                invalid={!!errors.contactEmail}
                            />
                            {!!errors.contactEmail && <Hint>{errors.contactEmail}</Hint>}
                        </Label>
                    </FieldWrap>
                    <Checkboxes>
                        <FieldWrap $appear>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={newsletterSignup}
                                    onChange={() => {
                                        setNewsletterSignup((c) => !c)
                                    }}
                                />
                                I&apos;d like to get newsletters from Streamr
                            </CheckboxLabel>
                        </FieldWrap>
                        <FieldWrap $appear>
                            <CheckboxLabel>
                                <Checkbox
                                    checked={termsApproved}
                                    onChange={() => {
                                        setFormData((fd) => ({
                                            ...fd,
                                            termsApproved: !fd.termsApproved,
                                        }))
                                    }}
                                />
                                I agree to the{' '}
                                <Link
                                    href={urls.documents.terms}
                                    blank
                                    look={LinkLook.Basic}
                                    css={css`
                                        --color: #0324ff;
                                    `}
                                >
                                    terms and conditions
                                </Link>
                            </CheckboxLabel>
                        </FieldWrap>
                    </Checkboxes>
                    <ButtonWrapper>
                        <PrimaryButton
                            disabled={!termsApproved || submitting}
                            busy={submitting}
                            type="submit"
                        >
                            {submitting ? 'Submitting' : 'Submit application'}
                        </PrimaryButton>
                    </ButtonWrapper>
                    <Message visible={resolution != null}>
                        {resolution === true ? (
                            <>
                                <Checkmark />
                                <span>All done! Your application has been submitted.</span>
                            </>
                        ) : (
                            resolution
                        )}
                    </Message>
                </Form>
                <HubSpotFormContainer
                    targetId={NEWSLETTER_FORM_CONTAINER_ID}
                    formId={process.env.GATSBY_HUBSPOT_NEWSLETTER_FORM_ID}
                    portalId={process.env.GATSBY_HUBSPOT_NEWSLETTER_PORTAL_ID}
                />
            </Container>
        </Background>
    )
}

const FieldWrap = styled.div`
    & + & {
        margin-top: 40px;
    }

    ${Hint} {
        color: #ff5c00;
        font-weight: ${Medium};
    }

    ${({ $bringToFront = false }) =>
        $bringToFront &&
        css`
            position: relative;
            z-index: 1;
        `}
`

const Checkboxes = styled.div`
    margin-top: 60px;

    ${FieldWrap} + ${FieldWrap} {
        margin-top: 16px;
    }
`

export default ApplicationForm
